import { useEffect, useState, useSyncExternalStore } from "react";

const KEY = "favorites";

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

const listeners = new Set<() => void>();
let cache: string[] = typeof window !== "undefined" ? read() : [];

function emit() {
  cache = read();
  for (const fn of listeners) fn();
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", e => {
    if (e.key === KEY) emit();
  });
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getSnapshot() {
  return cache;
}

function getServerSnapshot(): string[] {
  return [];
}

function write(next: string[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / disabled storage */
  }
  emit();
}

export function useFavorites() {
  const ids = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    ids,
    has: (id: string) => ids.includes(id),
    toggle: (id: string) => {
      const next = ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id];
      write(next);
    },
    clear: () => write([]),
  };
}

// Avoid layout-shift / hydration mismatch by deferring "favorite" UI to post-mount.
export function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
}
